import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { api } from "../api";
import { BIZ } from "../env";

interface StartSessionResponse {
  status: string;
  message?: string;
  guest_session_key: string;
  session_status_url: string;
}

interface SessionStatusResponse {
  state: "pending" | "active" | "completed";
  guest_session_key: string;
  charge_point_id: string;
  session_id?: number;
  ocpp_transaction_id?: string;
  start_time?: string;
  stop_time?: string;
  energy_kwh?: number;
  power_kw?: number;
  soc_percent?: number;
  cost?: number;
  currency?: string;
  updated_at?: string;
  latest_event?: string;
}

function fmt(value?: number, digits = 2) {
  if (value == null || Number.isNaN(value)) return "—";
  return value.toFixed(digits);
}

function fmtDate(value?: string) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function normalizeKey(value: string) {
  return value.trim();
}

function normalizePi(value: string) {
  return value.trim();
}

function normalizeCp(value: string) {
  return value.trim();
}

export default function GuestSessionStatusPage() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const autoStart = params.get("autostart") === "1";

  const [searchKey, setSearchKey] = useState(params.get("key") || "");
  const [searchPaymentIntentId, setSearchPaymentIntentId] = useState(params.get("payment_intent_id") || "");
  const [searchChargePointId, setSearchChargePointId] = useState(params.get("charge_point_id") || "");

  const [activeKey, setActiveKey] = useState(params.get("key") || "");
  const [activePaymentIntentId, setActivePaymentIntentId] = useState(params.get("payment_intent_id") || "");
  const [activeChargePointId, setActiveChargePointId] = useState(params.get("charge_point_id") || "");

  const [status, setStatus] = useState<SessionStatusResponse | null>(null);
  const [startResult, setStartResult] = useState<StartSessionResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const startedRef = useRef(false);

  const canStartSession =
    normalizePi(searchPaymentIntentId).length > 0 && normalizeCp(searchChargePointId).length > 0;
  const hasSearchCriteria =
    normalizeKey(searchKey).length > 0 ||
    (normalizePi(searchPaymentIntentId).length > 0 && normalizeCp(searchChargePointId).length > 0);

  const loadStatus = async (query: { key?: string; paymentIntentId?: string; chargePointId?: string }) => {
    if (!query.key && (!query.paymentIntentId || !query.chargePointId)) return;
    try {
      const searchParams = new URLSearchParams();
      if (query.key) {
        searchParams.set("key", query.key);
      } else {
        searchParams.set("payment_intent_id", query.paymentIntentId!);
        searchParams.set("charge_point_id", query.chargePointId!);
      }
      const data = await api<SessionStatusResponse>(`${BIZ}/api/v1/public/sessions/status?${searchParams.toString()}`);
      if (data) {
        setStatus(data);
        if (!query.key && data.guest_session_key) {
          setSearchKey(data.guest_session_key);
        }
      }
      setError(null);
    } catch (err) {
      setError((err as Error).message || "Unable to load session status");
    }
  };

  const updateBrowserUrl = (query: { key?: string; paymentIntentId?: string; chargePointId?: string }) => {
    const next = new URL(window.location.href);
    next.searchParams.delete("key");
    next.searchParams.delete("payment_intent_id");
    next.searchParams.delete("charge_point_id");
    if (query.key) {
      next.searchParams.set("key", query.key);
    } else {
      if (query.paymentIntentId) next.searchParams.set("payment_intent_id", query.paymentIntentId);
      if (query.chargePointId) next.searchParams.set("charge_point_id", query.chargePointId);
    }
    if (autoStart) next.searchParams.set("autostart", "1");
    window.history.replaceState(null, "", `${next.pathname}?${next.searchParams.toString()}`);
  };

  const searchSession = async () => {
    const key = normalizeKey(searchKey);
    const paymentIntentId = normalizePi(searchPaymentIntentId);
    const chargePointId = normalizeCp(searchChargePointId);
    if (!key && (!paymentIntentId || !chargePointId)) {
      setError("Enter either a guest session key, or payment intent + charge point.");
      return;
    }

    const query = key
      ? { key }
      : { paymentIntentId, chargePointId };

    setActiveKey(query.key || "");
    setActivePaymentIntentId(query.paymentIntentId || "");
    setActiveChargePointId(query.chargePointId || "");
    updateBrowserUrl(query);
    await loadStatus(query);
  };

  const startSession = async () => {
    if (!canStartSession) return;
    setLoading(true);
    try {
      const data = await api<StartSessionResponse>(`${BIZ}/api/v1/public/sessions/start`, {
        method: "POST",
        body: JSON.stringify({
          payment_intent_id: normalizePi(searchPaymentIntentId),
          charge_point_id: normalizeCp(searchChargePointId),
        }),
      });
      if (data) {
        setStartResult(data);
        setSearchKey(data.guest_session_key);
        setActiveKey(data.guest_session_key);
        setActivePaymentIntentId("");
        setActiveChargePointId("");
        updateBrowserUrl({ key: data.guest_session_key });
        await loadStatus({ key: data.guest_session_key });
      }
      startedRef.current = true;
      setError(null);
    } catch (err) {
      setError((err as Error).message || "Unable to start charging session");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      if (autoStart && canStartSession && !startedRef.current) {
        await startSession();
        return;
      }
      if (activeKey) {
        await loadStatus({ key: activeKey });
        return;
      }
      if (activePaymentIntentId && activeChargePointId) {
        await loadStatus({ paymentIntentId: activePaymentIntentId, chargePointId: activeChargePointId });
      }
    };
    void init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!activeKey && (!activePaymentIntentId || !activeChargePointId)) return;
    const interval = window.setInterval(() => {
      if (activeKey) {
        void loadStatus({ key: activeKey });
      } else {
        void loadStatus({ paymentIntentId: activePaymentIntentId, chargePointId: activeChargePointId });
      }
    }, 5000);
    return () => window.clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, activePaymentIntentId, activeChargePointId]);

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
      <Paper sx={{ width: "100%", maxWidth: 640, p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h5" fontWeight={600}>Session Status</Typography>

          <Alert severity="info">
            You can search by <strong>Guest Session Key</strong> or by <strong>PaymentIntent + Charge Point</strong>.
          </Alert>

          <TextField
            label="Guest Session Key"
            value={searchKey}
            onChange={(e) => setSearchKey(e.target.value)}
            placeholder="Paste your saved key"
            fullWidth
          />

          <Divider>or</Divider>

          <TextField
            label="Payment Intent ID"
            value={searchPaymentIntentId}
            onChange={(e) => setSearchPaymentIntentId(e.target.value)}
            placeholder="pi_..."
            fullWidth
          />
          <TextField
            label="Charge Point ID"
            value={searchChargePointId}
            onChange={(e) => setSearchChargePointId(e.target.value)}
            placeholder="CP-..."
            fullWidth
          />

          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => void searchSession()} disabled={!hasSearchCriteria || loading}>
              Search Session
            </Button>
            <Button
              variant="contained"
              disabled={!canStartSession || loading}
              onClick={() => void startSession()}
            >
              {loading ? "Starting…" : "Start Charging"}
            </Button>
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}

          {startResult && (
            <Alert severity={startResult.status === "Accepted" ? "success" : "warning"}>
              Start command: {startResult.status}
              {startResult.message ? ` — ${startResult.message}` : ""}
            </Alert>
          )}

          {status?.guest_session_key && (
            <Alert severity="info">
              <Typography variant="body2">
                <strong>Guest Session Key:</strong> {status.guest_session_key}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Save this key to check your session later.
              </Typography>
            </Alert>
          )}

          {!status ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={1}>
              <Typography><strong>State:</strong> {status.state}</Typography>
              <Typography><strong>Charge Point:</strong> {status.charge_point_id || "—"}</Typography>
              <Typography><strong>Latest event:</strong> {status.latest_event || "Waiting for charger updates…"}</Typography>
              <Typography><strong>Started:</strong> {fmtDate(status.start_time)}</Typography>
              <Typography><strong>Stopped:</strong> {fmtDate(status.stop_time)}</Typography>
              <Typography><strong>Energy:</strong> {fmt(status.energy_kwh, 3)} kWh</Typography>
              <Typography><strong>Power:</strong> {fmt(status.power_kw, 2)} kW</Typography>
              <Typography><strong>SOC:</strong> {fmt(status.soc_percent, 0)}%</Typography>
              <Typography><strong>Cost:</strong> {fmt(status.cost)} {status.currency || ""}</Typography>
              <Typography variant="caption" color="text.secondary">
                Updated: {fmtDate(status.updated_at)}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
